import { useState } from 'react';
import type { CategoryScores } from '@/domains/results/types';
import { DIAGNOSTIC_CATEGORIES } from '@/domains/assessment/constants';

export interface RadarChartProps {
  scores: CategoryScores;
  className?: string;
}

export function RadarChart({ scores, className = '' }: RadarChartProps) {
  const [showDataTable, setShowDataTable] = useState(false);

  // Dimensões do radar vetorial: viewBox com ampla folga de segurança sem textos internos
  const size = 300;
  const center = size / 2; // 150
  const maxRadius = 105;

  const categories = DIAGNOSTIC_CATEGORIES;
  const totalCategories = categories.length; // 6

  // Converte polar para cartesiano (x, y)
  const getCoordinates = (index: number, score: number, radius = maxRadius) => {
    // Começa no topo (-PI / 2)
    const angle = ((Math.PI * 2) / totalCategories) * index - Math.PI / 2;
    const distance = (score / 100) * radius;
    const x = center + distance * Math.cos(angle);
    const y = center + distance * Math.sin(angle);
    return { x, y, angle };
  };

  // Níveis concêntricos de referência (25%, 50%, 75%, 100%)
  const levels = [25, 50, 75, 100];

  // Gera o caminho do polígono de notas
  const polygonPoints = categories
    .map((cat, idx) => {
      const score = scores[cat.key] ?? 0;
      const { x, y } = getCoordinates(idx, score);
      return `${x},${y}`;
    })
    .join(' ');

  return (
    <div className={`flex flex-col items-center w-full min-w-0 ${className}`}>
      {/* Botão de acessibilidade para alternar visualização em tabela */}
      <div className="mb-2 w-full flex justify-end">
        <button
          type="button"
          onClick={() => setShowDataTable((prev) => !prev)}
          className="text-xs font-semibold text-brand-orange hover:text-brand-orangeSecondary underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange rounded p-1"
          aria-expanded={showDataTable}
          aria-controls="radar-accessible-table"
        >
          {showDataTable ? 'Ocultar tabela de dados' : 'Ver dados em formato de tabela'}
        </button>
      </div>

      {/* Gráfico Radar Vetorial puro sem textos internos para evitar qualquer corte lateral */}
      <div className="relative w-full max-w-[260px] sm:max-w-[280px] aspect-square flex items-center justify-center">
        <svg
          viewBox={`0 0 ${size} ${size}`}
          className="w-full h-full"
          role="img"
          aria-label="Gráfico radar demonstrando a pontuação do escritório nas seis dimensões estratégicas."
        >
          {/* Círculos / teias de fundo */}
          {levels.map((lvl) => {
            const points = categories
              .map((_, idx) => {
                const { x, y } = getCoordinates(idx, lvl);
                return `${x},${y}`;
              })
              .join(' ');
            return (
              <polygon
                key={lvl}
                points={points}
                fill="none"
                stroke="rgba(255, 255, 255, 0.14)"
                strokeWidth={1}
                strokeDasharray={lvl === 100 ? undefined : '3,3'}
              />
            );
          })}

          {/* Eixos radiais */}
          {categories.map((cat, idx) => {
            const { x, y } = getCoordinates(idx, 100);
            return (
              <line
                key={cat.key}
                x1={center}
                y1={center}
                x2={x}
                y2={y}
                stroke="rgba(255, 255, 255, 0.18)"
                strokeWidth={1}
              />
            );
          })}

          {/* Polígono preenchido dos scores do usuário */}
          <polygon
            points={polygonPoints}
            fill="rgba(254, 89, 31, 0.28)"
            stroke="#FE591F"
            strokeWidth={2.5}
            className="transition-all duration-700 ease-out"
          />

          {/* Pontos de dados nos vértices dos scores */}
          {categories.map((cat, idx) => {
            const score = scores[cat.key] ?? 0;
            const { x, y } = getCoordinates(idx, score);

            return (
              <circle
                key={`point-${cat.key}`}
                cx={x}
                cy={y}
                r={4.5}
                fill="#FE591F"
                stroke="#FFFFFF"
                strokeWidth={2}
              />
            );
          })}
        </svg>
      </div>

      {/* Legenda Externa Responsiva com as 6 Categorias e Percentuais */}
      <div
        data-testid="radar-legend"
        className="mt-5 grid w-full grid-cols-1 gap-2 sm:grid-cols-2 min-w-0"
        aria-label="Legenda com notas das dimensões"
      >
        {categories.map((cat) => {
          const score = scores[cat.key] ?? 0;
          return (
            <div
              key={cat.key}
              id={`radar-legend-${cat.key}`}
              data-testid={`radar-legend-${cat.key}`}
              className="flex items-center justify-between gap-3 rounded-lg border border-white/5 bg-navy-light/40 px-3 py-2 text-xs min-w-0 transition-colors hover:border-brand-orange/30"
            >
              <div className="flex items-center gap-2 min-w-0">
                <span
                  className="h-2 w-2 shrink-0 rounded-full bg-brand-orange shadow-[0_0_6px_rgba(254,89,31,0.6)]"
                  aria-hidden="true"
                />
                <span className="font-medium text-brand-offWhite leading-snug">
                  {cat.name}
                </span>
              </div>
              <span className="shrink-0 font-bold text-brand-orange text-xs tabular-nums">
                {score}%
              </span>
            </div>
          );
        })}
      </div>

      {/* Tabela de acessibilidade semântica */}
      <div
        id="radar-accessible-table"
        className={showDataTable ? 'mt-4 block w-full overflow-x-auto min-w-0' : 'sr-only'}
      >
        <table className="w-full text-left text-xs text-brand-offWhite border border-white/10 rounded-xl overflow-hidden">
          <caption className="py-2 font-bold text-white text-sm">
            Detalhamento de Notas por Dimensão Estratégica
          </caption>
          <thead className="bg-white/10 text-white uppercase text-[10px]">
            <tr>
              <th scope="col" className="px-3 py-2">Dimensão</th>
              <th scope="col" className="px-3 py-2 text-right">Nota Obtida</th>
              <th scope="col" className="px-3 py-2 text-right">Situação</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5 bg-navy-light/40">
            {categories.map((cat) => {
              const score = scores[cat.key] ?? 0;
              const status =
                score >= 65 ? 'Fortalecido' : score >= 40 ? 'Em desenvolvimento' : 'Ponto Crítico';

              return (
                <tr key={cat.key}>
                  <td className="px-3 py-2 font-medium text-white">{cat.name}</td>
                  <td className="px-3 py-2 text-right font-bold text-brand-orange">{score}%</td>
                  <td className="px-3 py-2 text-right">
                    <span
                      className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold ${
                        score < 40
                          ? 'bg-feedback-danger/20 text-feedback-danger'
                          : score >= 65
                            ? 'bg-feedback-success/20 text-feedback-success'
                            : 'bg-feedback-warning/20 text-feedback-warning'
                      }`}
                    >
                      {status}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
